'use client';

// P29.8 marketing features grid. 10 module cards in a 3x3 (md) / 1-col (mobile)
// layout (4th row holds AI Assistente). Hover lift + scroll-reveal stagger.
// Each card: lucide icon + module name + 1-sentence value prop + min-tier badge.

import { m, useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import {
  FileText,
  TrendingUp,
  Users,
  Utensils,
  Truck,
  ShieldCheck,
  Heart,
  ChartBar,
  Package,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';

type TierKey = 'silver' | 'gold' | 'platinum' | 'diamond' | 'titan';
type FeatureKey =
  | 'preventivi'
  | 'finanze'
  | 'staff'
  | 'foodBeverage'
  | 'trasporti'
  | 'haccp'
  | 'clienti'
  | 'analytics'
  | 'fornitori'
  | 'aiAssistente';

const FEATURES: { key: FeatureKey; Icon: LucideIcon; tier: TierKey }[] = [
  { key: 'preventivi', Icon: FileText, tier: 'silver' },
  { key: 'finanze', Icon: TrendingUp, tier: 'gold' },
  { key: 'staff', Icon: Users, tier: 'gold' },
  { key: 'foodBeverage', Icon: Utensils, tier: 'platinum' },
  { key: 'trasporti', Icon: Truck, tier: 'platinum' },
  { key: 'haccp', Icon: ShieldCheck, tier: 'diamond' },
  { key: 'clienti', Icon: Heart, tier: 'titan' },
  { key: 'analytics', Icon: ChartBar, tier: 'titan' },
  { key: 'fornitori', Icon: Package, tier: 'titan' },
  { key: 'aiAssistente', Icon: Sparkles, tier: 'titan' },
];

export function FeaturesGrid() {
  const t = useTranslations('marketing.features');
  const reduceMotion = useReducedMotion();

  const initial = reduceMotion ? false : { opacity: 0, y: 16 };
  const whileInView = { opacity: 1, y: 0 };

  return (
    <section id="features" data-testid="marketing-features" className="bg-slate-50 py-20">
      <div className="mx-auto max-w-6xl px-4">
        <div className="text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
            {t('sectionTitle')}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-600 md:text-base">
            {t('sectionSubtitle')}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, idx) => {
            const Icon = f.Icon;
            return (
              <m.article
                key={f.key}
                data-testid={`feature-card-${f.key}`}
                initial={initial}
                whileInView={whileInView}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.4, delay: (idx % 3) * 0.08 }}
                whileHover={
                  reduceMotion ? undefined : { y: -3, boxShadow: '0 6px 18px rgb(0 0 0 / 0.08)' }
                }
                className="rounded-lg border border-slate-200 bg-white p-5"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-slate-600 uppercase">
                    {t(`tierLabel.${f.tier}`)}
                  </span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-slate-900">
                  {t(`${f.key}.name`)}
                </h3>
                <p className="mt-1 text-sm text-slate-600">{t(`${f.key}.value`)}</p>
              </m.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
